<template>
    <HeaderComponent :title="affiliate.name + ': Affiliate Reports'" :actions="navActions" 
    />
    <DataTable
        :value="affiliate.affiliate_reports"
        tableStyle="min-width: 50rem"
    >
        <template #empty>
            <div class="flex flex-col items-center justify-center h-full">
                <h1 class="text-2xl font-bold">No reports found</h1>
                <p class="text-gray-500">No reports have been found for this affiliate.</p>
            </div>
        </template>
        <Column field="type" header="Type"></Column>
        <Column field="period" header="Period"></Column>
        <Column field="description" header="Description"></Column>
        <Column field="amount" header="Amount" :sortable="true">
            <template #body="slotProps">
                {{ CurrencyFormat(slotProps.data.amount) }}
            </template>
        </Column>
        <Column field="created" header="Created">
            <template #body="slotProps">
                {{ slotProps.data.created ? (new Date(slotProps.data.created)).toLocaleString(): '-' }}
            </template>
        </Column>
        <Column header="Actions" v-if="authUser.is_admin">
            <template #body="slotProps">
                <div class="flex gap-2">
                    <Link :href="`/admin/affiliate-reports/edit/${slotProps.data.id}`" class="bg-green-500 text-white px-4 py-2 rounded-md">
                        Edit
                    </Link>
                    <a @click="() => deleteReport(slotProps.data.id)" class="bg-red-500 text-white px-4 py-2 rounded-md cursor-pointer">
                        Delete
                    </a>
                </div>
            </template>
        </Column>
    </DataTable>
</template>

<script setup>
import { usePage, router, Link } from "@inertiajs/vue3";
import { computed } from "vue";
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import CurrencyFormat from "@/Utilities/CurrencyFormat";
import HeaderComponent from "@/Components/HeaderComponent.vue";

const page = usePage();

const authUser = computed(() => page.props.authUser);
const affiliate = computed(() => page.props.affiliate);
const total = computed(() => page.props.total);
const deleteReport = (id) => {
    if (!confirm("Are you sure you want to delete this report?")) return;
    router.delete(`/admin/affiliate-reports/delete/${id}`, {
        headers: {
            "X-CSRF-Token": page.props.csrfToken,
        },
    });
};

const navActions = computed(() => {
    let actions = [
        {label: 'Balance: ' + CurrencyFormat(total.value), class: 'hover:[transform:unset] bg-[unset] text-2xl font-bold'},
        {label: 'Export to CSV', click: exportToCSV, class: 'bg-blue-500 text-white'}
    ];
    if (authUser.value.is_admin) {
        actions.push({label: 'Add Report', click: () => router.get(`/admin/affiliate-reports/add/${affiliate.value.short_uuid}`), class: 'bg-green-500 text-white'});
    }
    if (affiliate.value.affiliate_type_id === 1) {
        actions.push({label: 'Request Payout', click: () => router.get(`/affiliates/${affiliate.value.short_uuid}/request-payout`), class: 'bg-yellow-500 text-white'});
    }

    return actions;
})

const exportToCSV = () => {
    const reports = affiliate.value.affiliate_reports;
    const csvContent =
        "data:text/csv;charset=utf-8," +
        ["ID,Type,Period,Description,Amount"]
            .concat(
                reports.map(
                    (report) =>
                        `${report.id},${report.type},${report.period},${report.description},${CurrencyFormat(report.amount)}`,
                ),
            )
            .join("\n");

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", "affiliate_reports.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
};
</script>
